/**
 * i18n-dom — aplica traduções no DOM via data-i18n attributes.
 *
 * Convenção de markup:
 *
 *   <h1 data-i18n="hero.title">Texto PT original (fallback se chave faltar)</h1>
 *   <input data-i18n-attr="placeholder:contact.namePlaceholder" />
 *   <a data-i18n-attr="aria-label:navigation.openMenu;title:common.menu">…</a>
 *
 * - `data-i18n="<ns.key>"` substitui o textContent do elemento.
 * - `data-i18n-attr="<attr>:<ns.key>[;<attr>:<ns.key>...]"` substitui atributos.
 *
 * Se a chave não existir nem em PT nem em EN, mantém o conteúdo original
 * (não escreve a chave nua na UI).
 */

import { t, onLanguageChange } from './i18n';

const KEY_ATTR = 'data-i18n';
const ATTR_ATTR = 'data-i18n-attr';
const ORIGINAL_TEXT_ATTR = 'data-i18n-orig';

/**
 * Salva o textContent original em data-i18n-orig se ainda não estiver salvo.
 * Permite restaurar conteúdo se a chave faltar futuramente.
 */
function rememberOriginalText(el: Element): string {
  let orig = el.getAttribute(ORIGINAL_TEXT_ATTR);
  if (orig === null) {
    orig = el.textContent ?? '';
    el.setAttribute(ORIGINAL_TEXT_ATTR, orig);
  }
  return orig;
}

function translateOrFallback(key: string, fallback: string): string {
  const value = t(key);
  // i18next devolve a chave quando não acha — detecta isso comparando.
  if (value === key) return fallback;
  return value;
}

function applyTextKey(el: Element): void {
  const key = el.getAttribute(KEY_ATTR);
  if (!key) return;
  const fallback = rememberOriginalText(el);
  const value = translateOrFallback(key, fallback);
  if (el.textContent !== value) {
    el.textContent = value;
  }
}

function applyAttrKey(el: Element): void {
  const spec = el.getAttribute(ATTR_ATTR);
  if (!spec) return;
  // "placeholder:contact.name;title:common.menu" → [[placeholder, contact.name], [title, common.menu]]
  for (const pair of spec.split(';').map((s) => s.trim()).filter(Boolean)) {
    const idx = pair.indexOf(':');
    if (idx <= 0) continue;
    const attr = pair.slice(0, idx).trim();
    const key = pair.slice(idx + 1).trim();
    if (!attr || !key) continue;
    const origAttrName = `data-i18n-orig-${attr}`;
    let fallback = el.getAttribute(origAttrName);
    if (fallback === null) {
      fallback = el.getAttribute(attr) ?? '';
      el.setAttribute(origAttrName, fallback);
    }
    const value = translateOrFallback(key, fallback);
    if (el.getAttribute(attr) !== value) {
      el.setAttribute(attr, value);
    }
  }
}

/**
 * Aplica traduções em todos os `[data-i18n]` e `[data-i18n-attr]` dentro de `root`.
 * Padrão: root = document.
 */
export function applyI18nToDom(root: ParentNode = document): void {
  const textNodes = root.querySelectorAll(`[${KEY_ATTR}]`);
  for (const el of Array.from(textNodes)) applyTextKey(el);
  const attrNodes = root.querySelectorAll(`[${ATTR_ATTR}]`);
  for (const el of Array.from(attrNodes)) applyAttrKey(el);
}

/**
 * Liga o re-apply automático em `languageChanged`. Retorna cleanup.
 * Também executa um apply inicial para o idioma corrente.
 */
export function bindI18nAutoApply(root: ParentNode = document): () => void {
  // Apply inicial sincronamente para o idioma atual
  applyI18nToDom(root);
  // Re-apply em toda troca de idioma
  const unsubscribe = onLanguageChange(() => {
    applyI18nToDom(root);
  });
  return unsubscribe;
}

/**
 * Helper: retorna o idioma atual (re-export para conveniência).
 */
export { getLanguage } from './i18n';
